import { useEffect, useMemo, useState } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Sparkles, Rocket, ShieldCheck, Timer, ChevronRight, ChevronLeft,
  CheckCircle2, Loader2, Zap, ArrowRight, Mail, Lock, ExternalLink,
  User, Store, MapPin, Phone, MessageCircle, Link as LinkIcon, Star,
} from "lucide-react";
import { toast } from "sonner";
import { launchVerticals, launchApply, launchStep, launchOtpSend, launchOtpVerify } from "../api";
import axios from "axios";
const API_ROOT = process.env.REACT_APP_BACKEND_URL;

const GOALS = [
  "Receive more calls", "Generate qualified leads", "Book appointments",
  "Sell products", "Accept restaurant/food orders", "Take hotel reservations",
  "Display services or portfolio", "Provide quotations",
  "Receive WhatsApp inquiries", "Serve customers in multiple languages",
  "Improve local visibility", "Add an AI assistant", "Replace an outdated website",
];

const COUNTRIES = [
  { code: "US", label: "United States (USD)" },
  { code: "IN", label: "India (INR)" },
  { code: "OTHER", label: "Other" },
];

// Common states/provinces for geo-clean data. Free-text still allowed for
// "OTHER" so we never block a real user in an unlisted country.
const US_STATES = [
  "AL", "AK", "AZ", "AR", "CA", "CO", "CT", "DE", "FL", "GA", "HI", "ID", "IL",
  "IN", "IA", "KS", "KY", "LA", "ME", "MD", "MA", "MI", "MN", "MS", "MO", "MT",
  "NE", "NV", "NH", "NJ", "NM", "NY", "NC", "ND", "OH", "OK", "OR", "PA", "RI",
  "SC", "SD", "TN", "TX", "UT", "VT", "VA", "WA", "WV", "WI", "WY", "DC",
];
const IN_STATES = [
  "Andhra Pradesh", "Arunachal Pradesh", "Assam", "Bihar", "Chhattisgarh",
  "Goa", "Gujarat", "Haryana", "Himachal Pradesh", "Jharkhand", "Karnataka",
  "Kerala", "Madhya Pradesh", "Maharashtra", "Manipur", "Meghalaya", "Mizoram",
  "Nagaland", "Odisha", "Punjab", "Rajasthan", "Sikkim", "Tamil Nadu",
  "Telangana", "Tripura", "Uttar Pradesh", "Uttarakhand", "West Bengal",
  "Delhi", "Jammu & Kashmir", "Ladakh", "Puducherry", "Chandigarh",
];

// ---------------------------------------------------------------------------
// Landing hero — swaps to <Wizard /> once user taps "Start my application".
// ---------------------------------------------------------------------------
export default function LaunchProgramPage() {
  const [searchParams] = useSearchParams();
  const preselectVertical = searchParams.get("vertical");
  const [phase, setPhase] = useState(preselectVertical ? "wizard" : "landing"); // landing | wizard | otp | done
  const [verticals, setVerticals] = useState([]);
  const [heroCollage, setHeroCollage] = useState(null);
  const [sitesBuilt, setSitesBuilt] = useState([]);
  const [appId, setAppId] = useState(null);
  const [publicToken, setPublicToken] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    launchVerticals().then(d => {
      setVerticals(d.verticals || []);
      setHeroCollage(d.hero_collage || null);
      setSitesBuilt(d.sites_built || []);
    });
  }, []);

  if (phase === "landing") return <LandingHero verticals={verticals} heroCollage={heroCollage} sitesBuilt={sitesBuilt} onStart={() => setPhase("wizard")} />;
  if (phase === "wizard") return (
    <Wizard verticals={verticals} preselectVertical={preselectVertical}
      onSubmitted={(id, tok) => { setAppId(id); setPublicToken(tok); setPhase("otp"); }} />
  );
  if (phase === "otp") return (
    <OtpStep appId={appId} publicToken={publicToken}
      onVerified={() => { setPhase("done"); }} />
  );
  return <DoneCard publicToken={publicToken} onView={() => navigate(`/launch/preview/${publicToken}`)} />;
}

// ---------------------------------------------------------------------------
function LandingHero({ verticals, heroCollage, sitesBuilt, onStart }) {
  return (
    <div className="min-h-screen bg-gradient-to-b from-sky-50 via-white to-emerald-50 text-slate-900 relative overflow-hidden"
         data-testid="launch-landing">
      <div className="absolute inset-0 pointer-events-none opacity-60"
           style={{
             backgroundImage: "radial-gradient(circle at 15% 10%, rgba(56,189,248,.18), transparent 55%), radial-gradient(circle at 85% 30%, rgba(52,211,153,.15), transparent 55%)",
           }} />
      <div className="relative max-w-7xl mx-auto px-6 pt-28 pb-16 grid lg:grid-cols-12 gap-10 items-start">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
                    className="lg:col-span-7">
          <Badge className="bg-sky-100 border border-sky-300 text-sky-800 uppercase tracking-widest text-[10px] font-bold" data-testid="launch-badge">
            <Sparkles size={11} className="mr-1" /> WebsiteTech AI Launch Program
          </Badge>
          <h1 className="mt-6 text-4xl sm:text-5xl lg:text-6xl font-black leading-[1.05] max-w-4xl text-slate-900">
            See your AI-generated website preview{" "}
            <span className="bg-gradient-to-r from-sky-600 to-emerald-600 bg-clip-text text-transparent">free</span>{" "}
            — before deciding to launch.
          </h1>
          <p className="mt-6 text-lg text-slate-600 max-w-2xl">
            Apply in three minutes. Our AI drafts a homepage concept, a page plan,
            headline copy and a hero image based on your business. You verify every
            fact before anything goes live. Zero design fee for qualified starter
            plans.
          </p>
          <div className="mt-8 flex flex-wrap items-center gap-3">
            <Button onClick={onStart} size="lg"
              className="bg-slate-900 text-white hover:bg-slate-800 font-bold text-base px-8 py-6"
              data-testid="launch-start-cta">
              <Rocket size={18} className="mr-2" /> Start my 3-minute application
            </Button>
            <Link to="/launch/showcase" data-testid="launch-showcase-link"
                  className="inline-flex items-center gap-1.5 text-sm text-slate-700 hover:text-sky-700 font-semibold">
              <ExternalLink size={14} /> See sites we've built
            </Link>
          </div>
          <div className="mt-3 text-xs text-slate-500 flex items-center gap-1.5">
            <ShieldCheck size={13} className="text-emerald-600" />
            No payment required · No credit card · Human review before launch
          </div>
        </motion.div>

        {/* Top-right hero graphic: the 16-vertical mockup collage (clickable → /launch/designs) */}
        <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.7, delay: 0.15 }}
                    className="lg:col-span-5" data-testid="launch-hero-collage">
          {heroCollage ? (
            <Link to="/launch/designs" data-testid="launch-hero-collage-link">
              <div className="rounded-2xl overflow-hidden border border-slate-200 shadow-xl bg-white relative group cursor-pointer">
                <img src={heroCollage} alt="16 sample designs — tap to browse the design board"
                     className="w-full h-auto block transition-transform duration-700 group-hover:scale-[1.02]" />
                <div className="absolute inset-0 bg-slate-900/0 group-hover:bg-slate-900/10 transition" />
                <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-white/95 via-white/70 to-transparent p-4">
                  <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">Recent designs</div>
                  <div className="flex items-center justify-between gap-2">
                    <div className="text-sm text-slate-900 font-semibold">16 industries · one launch program</div>
                    <span className="text-xs text-sky-700 font-bold flex items-center gap-1 opacity-0 group-hover:opacity-100 transition">
                      Browse board <ArrowRight size={12} />
                    </span>
                  </div>
                </div>
              </div>
            </Link>
          ) : (
            <div className="rounded-2xl border border-slate-200 bg-white aspect-[4/3] animate-pulse" />
          )}
          <Link to="/launch/designs" data-testid="launch-designs-cta"
                className="mt-3 inline-flex items-center gap-1.5 text-xs text-sky-700 hover:text-sky-900 font-semibold">
            <Sparkles size={12} /> Browse every design as its own board →
          </Link>
        </motion.div>
      </div>

      <div className="relative max-w-7xl mx-auto px-6 pb-16">
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.35 }}>
          <div className="grid md:grid-cols-4 gap-3">
            {[
              { icon: Timer, title: "3 minutes", note: "Short form. Skip fields you don't have yet." },
              { icon: Sparkles, title: "AI preview", note: "Personalised homepage draft on-screen in seconds." },
              { icon: ShieldCheck, title: "Fact ledger", note: "Nothing publishes until you verify it." },
              { icon: Zap, title: "No design fee", note: "For qualified starter plans." },
            ].map((f) => (
              <div key={f.title} className="p-4 rounded-xl bg-white border border-slate-200 shadow-sm">
                <f.icon size={18} className="text-sky-600" />
                <div className="mt-2 font-bold text-slate-900">{f.title}</div>
                <div className="text-xs text-slate-500 mt-1">{f.note}</div>
              </div>
            ))}
          </div>

          {/* Sites we've built — small quick showcase strip */}
          {(sitesBuilt || []).length > 0 && (
            <div className="mt-14" data-testid="launch-sites-built-strip">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div className="text-xs uppercase tracking-widest text-slate-500 font-bold">Real sites we've built</div>
                <Link to="/launch/showcase" className="text-xs text-sky-700 hover:text-sky-900 font-semibold flex items-center gap-1">
                  Full showcase <ArrowRight size={11} />
                </Link>
              </div>
              <div className="mt-3 grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {sitesBuilt.slice(0, 3).map((s) => (
                  <a key={s.slug} href={s.url} target="_blank" rel="noreferrer"
                     data-testid={`site-built-${s.slug}`}
                     className="group rounded-xl overflow-hidden border border-slate-200 bg-white shadow-sm hover:shadow-md hover:border-sky-300 transition">
                    <div className="aspect-[16/9] overflow-hidden bg-slate-100">
                      <img src={s.screenshot} alt={s.name}
                           className="w-full h-full object-cover object-top group-hover:scale-[1.02] transition-transform duration-500" />
                    </div>
                    <div className="p-3">
                      <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">{s.vertical}</div>
                      <div className="text-sm font-bold text-slate-900 mt-0.5 flex items-center gap-1">{s.name} <ExternalLink size={11} className="text-slate-400 group-hover:text-sky-600" /></div>
                    </div>
                  </a>
                ))}
              </div>
            </div>
          )}

          <div className="mt-16">
            <div className="text-xs uppercase tracking-widest text-slate-500 font-bold">Built for</div>
            <div className="mt-3 flex flex-wrap gap-2">
              {(verticals || []).filter(v => v.slug !== "generic").map(v => (
                <Badge key={v.slug} data-testid={`vertical-chip-${v.slug}`}
                  variant="outline" className="border-slate-300 text-slate-700 text-xs bg-white">
                  {v.label}
                </Badge>
              ))}
            </div>
          </div>

          <div className="mt-16 grid md:grid-cols-2 gap-4">
            <ScopeCard testid="scope-included" title="Included in the free preview" tone="ok" items={[
              "Personalised homepage draft (copy + hero)",
              "Recommended page plan (up to 5 pages)",
              "Business readiness score & missing-info list",
              "One AI-suggested visual direction",
              "Editable in your customer portal — nothing publishes automatically",
            ]} />
            <ScopeCard testid="scope-excluded" title="Excluded / not in starter (paid)" tone="warn" items={[
              "Ecommerce, ordering, booking systems",
              "Custom applications & databases",
              "AI voice / telephone agents",
              "Full multilingual site production",
              "Membership portals & complex integrations",
            ]} />
          </div>
        </motion.div>
      </div>
    </div>
  );
}

function ScopeCard({ title, items, tone, testid }) {
  const border = tone === "ok" ? "border-emerald-200" : "border-amber-200";
  const badge = tone === "ok" ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700";
  const bullet = tone === "ok" ? "text-emerald-600" : "text-amber-600";
  return (
    <Card className={`bg-white ${border} border p-5 shadow-sm`} data-testid={testid}>
      <div className={`inline-block text-[10px] uppercase tracking-widest font-bold ${badge} rounded-full px-2 py-1`}>{title}</div>
      <ul className="mt-3 space-y-2 text-sm text-slate-700">
        {items.map((i) => (
          <li key={i} className="flex items-start gap-2">
            <CheckCircle2 size={15} className={`${bullet} mt-0.5 shrink-0`} /> <span>{i}</span>
          </li>
        ))}
      </ul>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Wizard — steps 1..4 with progress bar, save-and-resume after OTP.
// ---------------------------------------------------------------------------
function Wizard({ verticals, preselectVertical, onSubmitted }) {
  const [step, setStep] = useState(1);
  const [appId, setAppId] = useState(null);
  const [publicToken, setPublicToken] = useState(null);
  const [busy, setBusy] = useState(false);

  const [s1, setS1] = useState({
    business_name: "", contact_name: "", contact_role: "",
    email: "", phone: "", country: "US", state_region: "", city: "",
    vertical: preselectVertical || "generic", existing_website: "", google_business_url: "",
    social_urls: [], new_or_redesign: "new", preferred_language: "en",
  });
  const [s2, setS2] = useState({ goals: [] });
  const [s3, setS3] = useState({
    desired_pages: ["Home", "About", "Services", "Contact"],
    primary_services: "", service_locations: "",
    preferred_colors: "", has_logo: false, has_photos: false,
    example_websites: "", required_languages: [], special_functions: [],
    launch_date_target: "",
  });
  const [s4, setS4] = useState({
    business_stage: "operating", owns_domain: null, approver_name: "",
    scope_type: "informational", needs_ecommerce: false,
    can_verify_facts: true, agrees_to_starter_scope: true,
    consent_terms: false, consent_privacy: false, consent_marketing: false,
  });


  const submitStep1 = async () => {
    if (!s1.business_name || !s1.contact_name || !s1.email) {
      toast.error("Please fill in business name, your name and email.");
      return;
    }
    setBusy(true);
    try {
      const d = await launchApply(s1);
      setAppId(d.app_id);
      setPublicToken(d.public_token);
      setStep(2);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start application.");
    } finally { setBusy(false); }
  };

  const submitStep = async (n, payload) => {
    setBusy(true);
    try {
      await launchStep(appId, n, payload);
      if (n < 4) setStep(n + 1);
      else onSubmitted(appId, publicToken); // move to OTP phase
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save this step.");
    } finally { setBusy(false); }
  };

  const STEP_LABELS = ["Basic Information", "Business Details", "Review & Customize", "Create Website"];
  return (
    <div className="min-h-screen bg-gradient-to-b from-sky-50 via-white to-emerald-50 py-12 px-4" data-testid="launch-wizard">
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-8">
          <h1 className="text-3xl sm:text-4xl font-black text-slate-900 leading-tight">Create Your <span className="text-slate-900">Business Website</span></h1>
          <p className="mt-2 text-sm text-slate-600 max-w-xl mx-auto">Tell us a few details. Our AI will design a beautiful, ready-to-edit website for your business in minutes.</p>
          <div className="mt-3 flex flex-wrap justify-center gap-4 text-xs">
            <span className="flex items-center gap-1 text-emerald-700"><CheckCircle2 size={13} /> Fast & Easy</span>
            <span className="flex items-center gap-1 text-emerald-700"><CheckCircle2 size={13} /> AI Powered</span>
            <span className="flex items-center gap-1 text-emerald-700"><CheckCircle2 size={13} /> No Technical Skills Needed</span>
          </div>
        </div>
        <div className="flex items-center justify-between mb-8 max-w-3xl mx-auto">
          {STEP_LABELS.map((label, i) => {
            const n = i + 1;
            const done = step > n, active = step === n;
            return (
              <div key={label} className="flex-1 flex items-center">
                <div className="flex flex-col items-center flex-1">
                  <div data-testid={`stepper-${n}`} className={`w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold border-2 transition ${
                    done ? "bg-sky-600 border-sky-600 text-white" :
                    active ? "bg-sky-600 border-sky-600 text-white ring-4 ring-sky-100" :
                    "bg-white border-slate-300 text-slate-400"}`}>
                    {done ? <CheckCircle2 size={16} /> : n}
                  </div>
                  <div className={`mt-2 text-xs font-semibold text-center ${active ? "text-sky-700" : done ? "text-slate-700" : "text-slate-400"}`}>{label}</div>
                </div>
                {i < STEP_LABELS.length - 1 && (
                  <div className={`h-0.5 flex-1 mx-1 -mt-6 ${done ? "bg-sky-600" : "bg-slate-200"}`} />
                )}
              </div>
            );
          })}
        </div>

        <Card className="p-6 sm:p-8 bg-white shadow-sm">
          {step === 1 && (
            <Step1 verticals={verticals} value={s1} onChange={setS1} onNext={submitStep1} busy={busy} />
          )}
          {step === 2 && (
            <Step2 value={s2} onChange={setS2} onPrev={() => setStep(1)}
              onNext={() => submitStep(2, s2)} busy={busy} />
          )}
          {step === 3 && (
            <Step3 vertical={s1.vertical} value={s3} onChange={setS3}
              onPrev={() => setStep(2)} onNext={() => submitStep(3, s3)} busy={busy} />
          )}
          {step === 4 && (
            <Step4 value={s4} onChange={setS4}
              onPrev={() => setStep(3)} onNext={() => submitStep(4, s4)} busy={busy} />
          )}
        </Card>

        {/* Bottom feature strip matching the mock */}
        <div className="mt-10 grid grid-cols-2 sm:grid-cols-5 gap-3 max-w-4xl mx-auto text-center" data-testid="wizard-feature-strip">
          {[
            { icon: "🎨", title: "Beautiful Designs", note: "Professionally designed for your industry" },
            { icon: "📱", title: "Mobile Friendly", note: "Looks great on all devices" },
            { icon: "🔍", title: "SEO Optimized", note: "Helps customers find you" },
            { icon: "✏️", title: "Easy to Edit", note: "Update anytime" },
            { icon: "❤️", title: "Get Online Faster", note: "Grow your business" },
          ].map((f) => (
            <div key={f.title} className="p-3">
              <div className="text-2xl">{f.icon}</div>
              <div className="mt-1 text-sm font-bold text-slate-800">{f.title}</div>
              <div className="text-[11px] text-slate-500 leading-snug">{f.note}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// --- Step components ---
function Field({ label, hint, required, children, testid }) {
  return (
    <label className="block" data-testid={testid ? `field-${testid}` : undefined}>
      <div className="text-sm font-semibold text-slate-800">
        {label}{required && <span className="text-rose-600 ml-1">*</span>}
      </div>
      {hint && <div className="text-xs text-slate-500 mb-1.5 mt-0.5">{hint}</div>}
      <div className="mt-1">{children}</div>
    </label>
  );
}

function Step1({ verticals, value, onChange, onNext, busy }) {
  const set = (k) => (e) => onChange({ ...value, [k]: e.target ? e.target.value : e });
  const [tagline, setTagline] = useState(value.tagline || "");
  const [shortDesc, setShortDesc] = useState(value.short_description || "");
  const [selectedKw, setSelectedKw] = useState(value.selected_keywords || []);
  const [seo, setSeo] = useState(null);
  const [seoLoading, setSeoLoading] = useState(false);

  useEffect(() => {
    const canSuggest = value.vertical && (value.city || value.state_region || value.country);
    if (!canSuggest) return;
    let cancel = false;
    const t = setTimeout(async () => {
      setSeoLoading(true);
      try {
        const { data } = await axios.post(`${API_ROOT}/api/launch/seo/suggest`, {
          vertical: value.vertical, country: value.country,
          state_region: value.state_region, city: value.city,
          business_name: value.business_name, tagline, short_description: shortDesc,
        });
        if (!cancel) setSeo(data);
      } catch {} finally { if (!cancel) setSeoLoading(false); }
    }, 500);
    return () => { cancel = true; clearTimeout(t); };
    // eslint-disable-next-line
  }, [value.vertical, value.city, value.state_region, value.country, value.business_name, tagline, shortDesc]);

  const toggleKw = (k) => setSelectedKw((p) => p.includes(k) ? p.filter(x => x !== k) : [...p, k]);
  const submit = () => {
    onChange({ ...value, tagline, short_description: shortDesc, selected_keywords: selectedKw });
    onNext();
  };

  return (
    <div className="space-y-6" data-testid="step1-new">
      <div className="bg-slate-50 rounded-xl p-5 sm:p-6 border border-slate-200">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-sky-100 text-sky-700 flex items-center justify-center"><User size={18} /></div>
          <div>
            <div className="text-lg font-bold text-slate-900">Your Information</div>
            <div className="text-xs text-slate-500">We'll use this to create and communicate about your website.</div>
          </div>
        </div>
        <div className="grid sm:grid-cols-3 gap-4 mt-5">
          <Field label="Your Name" required testid="contact-name"><Input value={value.contact_name} onChange={set("contact_name")} placeholder="John Doe" data-testid="input-contact-name" /></Field>
          <Field label="Email Address" required testid="email">
            <div className="relative"><Mail size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input type="email" value={value.email} onChange={set("email")} placeholder="you@example.com" className="pl-9" data-testid="input-email" /></div>
          </Field>
          <Field label="Phone Number" testid="phone"><div className="relative"><Phone size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input value={value.phone} onChange={set("phone")} placeholder="(555) 123-4567" className="pl-9" /></div>
          </Field>
          <Field label="Your role at the business" testid="contact-role"><Input value={value.contact_role} onChange={set("contact_role")} placeholder="Owner, manager..." /></Field>
          <Field label="Mobile / WhatsApp" testid="whatsapp"><div className="relative"><MessageCircle size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input value={value.phone} onChange={set("phone")} placeholder="+1 555 123 4567" className="pl-9" /></div>
          </Field>
          <Field label="New site or redesign?" testid="new-redesign">
            <select value={value.new_or_redesign} onChange={set("new_or_redesign")} className="w-full h-10 border border-slate-300 rounded-md px-3 text-sm bg-white">
              <option value="new">New website</option><option value="redesign">Redesign existing site</option>
            </select>
          </Field>
        </div>
        <div className="mt-4 rounded-lg bg-sky-50 border border-sky-200 p-3 flex items-start gap-3">
          <div className="w-9 h-9 rounded-full bg-white flex items-center justify-center shrink-0"><LinkIcon size={15} className="text-sky-600" /></div>
          <div className="flex-1">
            <div className="text-sm font-bold text-slate-800">Already have a website? <span className="text-slate-500 font-normal">(Optional)</span></div>
            <div className="text-xs text-slate-500">Paste your current URL and we'll use it as a starting point.</div>
            <Input value={value.existing_website} onChange={set("existing_website")} placeholder="https://" className="mt-2 bg-white" data-testid="input-existing-website" />
          </div>
        </div>
      </div>

      <div className="bg-emerald-50/40 rounded-xl p-5 sm:p-6 border border-emerald-100">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center"><Store size={18} /></div>
          <div>
            <div className="text-lg font-bold text-slate-900">Business Information</div>
            <div className="text-xs text-slate-500">Tell us about your business. Keep it simple — our AI will do the rest.</div>
          </div>
        </div>
        <div className="grid sm:grid-cols-2 gap-4 mt-5">
          <Field label="Business Name" required testid="business-name"><Input value={value.business_name} onChange={set("business_name")} placeholder="Your Business Name" data-testid="input-business-name" /></Field>
          <Field label="Select a Category" required testid="vertical">
            <select value={value.vertical} onChange={set("vertical")} className="w-full h-10 border border-slate-300 rounded-md px-3 text-sm bg-white" data-testid="select-vertical">
              {verticals.map(v => <option key={v.slug} value={v.slug}>{v.label}</option>)}
            </select>
          </Field>
          <Field label="Country" required testid="country">
            <select value={value.country} onChange={set("country")} className="w-full h-10 border border-slate-300 rounded-md px-3 text-sm bg-white" data-testid="select-country">
              {COUNTRIES.map(c => <option key={c.code} value={c.code}>{c.label}</option>)}
            </select>
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="State / Province" testid="state">
              {value.country === "US" ? (
                <select
                  value={value.state_region}
                  onChange={set("state_region")}
                  className="w-full h-10 border border-slate-300 rounded-md px-3 text-sm bg-white"
                  data-testid="select-state-region"
                >
                  <option value="">Select state</option>
                  {US_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              ) : value.country === "IN" ? (
                <select
                  value={value.state_region}
                  onChange={set("state_region")}
                  className="w-full h-10 border border-slate-300 rounded-md px-3 text-sm bg-white"
                  data-testid="select-state-region"
                >
                  <option value="">Select state</option>
                  {IN_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              ) : (
                <Input value={value.state_region} onChange={set("state_region")} placeholder="e.g. NY / Kerala" data-testid="input-state-region" />
              )}
            </Field>
            <Field label="City" testid="city"><div className="relative"><MapPin size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input value={value.city} onChange={set("city")} placeholder="Hauppauge" className="pl-8" data-testid="input-city" /></div>
            </Field>
          </div>
          <Field label="Tagline (Optional)"><Input value={tagline} onChange={(e) => setTagline(e.target.value)} placeholder="A short phrase about your business" />
            <div className="text-[11px] text-slate-500 mt-1">Example: "Quality Service You Can Trust"</div>
          </Field>
          <Field label="Google Business URL (optional)" testid="gbp"><Input value={value.google_business_url} onChange={set("google_business_url")} placeholder="https://g.page/..." /></Field>
        </div>
        <div className="mt-4">
          <Field label="Short Description">
            <div className="text-xs text-slate-500 mb-1.5">Tell us a few sentences about what you do. Don't worry about perfect wording — our AI will help improve it.</div>
            <textarea rows={3} value={shortDesc} onChange={(e) => setShortDesc(e.target.value.slice(0, 300))}
              className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm bg-white"
              placeholder="Type a short description of your business, products or services..." data-testid="input-short-description" />
            <div className="text-right text-[11px] text-slate-400 mt-1">{shortDesc.length} / 300</div>
          </Field>
        </div>
      </div>

      <div className="bg-violet-50/40 rounded-xl p-5 sm:p-6 border border-violet-100" data-testid="step1-seo-suggestions">
        <div className="flex items-center gap-3 justify-between flex-wrap">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-violet-100 text-violet-700 flex items-center justify-center"><Star size={18} /></div>
            <div>
              <div className="text-lg font-bold text-slate-900">Suggested Keywords for Your <span className="text-violet-700">Website</span></div>
              <div className="text-xs text-slate-500">Based on your business category & location. Tap the ones that fit.</div>
            </div>
          </div>
          <div className="text-[10px] uppercase tracking-widest text-violet-700 font-bold bg-white border border-violet-200 px-2 py-1 rounded-full flex items-center gap-1">
            {seoLoading ? <Loader2 size={11} className="animate-spin" /> : <Sparkles size={11} />} Generated by AI
          </div>
        </div>
        {seo?.page_title && (
          <div className="mt-4 rounded-lg bg-white border border-violet-200 p-3 text-sm" data-testid="step1-seo-title">
            <div className="text-[10px] uppercase tracking-widest text-violet-700 font-bold">Suggested page title (SEO)</div>
            <div className="text-slate-900 font-semibold mt-0.5">{seo.page_title}</div>
            <div className="text-xs text-slate-500 mt-1">{seo.meta_description}</div>
          </div>
        )}
        <div className="mt-4 flex flex-wrap gap-2">
          {(seo?.keywords || []).map((k) => {
            const on = selectedKw.includes(k);
            return (
              <button key={k} type="button" onClick={() => toggleKw(k)}
                data-testid={`kw-${k.replace(/[^a-z0-9]+/gi, '-')}`}
                className={`text-xs px-3 py-1.5 rounded-full border transition ${on
                    ? "bg-violet-600 text-white border-violet-600 font-semibold"
                    : "bg-white text-slate-700 border-slate-300 hover:border-violet-400"}`}>
                {k}
              </button>
            );
          })}
          {!seo && !seoLoading && (
            <div className="text-xs text-slate-500 italic">Pick a category + city — we'll surface keywords here.</div>
          )}
        </div>
      </div>

      <div className="flex justify-end">
        <Button onClick={submit} disabled={busy || !value.business_name || !value.contact_name || !value.email}
                className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold px-8 py-6 text-base rounded-full"
                data-testid="wizard-next-1">
          {busy ? <Loader2 className="mr-2 animate-spin" size={16} /> : <Sparkles size={16} className="mr-2" />}
          Create My Website <ArrowRight size={16} className="ml-2" />
        </Button>
      </div>
    </div>
  );
}



function Step2({ value, onChange, onPrev, onNext, busy }) {
  const toggle = (g) => {
    const has = value.goals.includes(g);
    if (has) onChange({ goals: value.goals.filter(x => x !== g) });
    else if (value.goals.length < 3) onChange({ goals: [...value.goals, g] });
    else toast.info("You can pick up to 3 primary goals.");
  };
  return (
    <div>
      <h2 className="text-2xl font-black text-slate-900">Pick up to 3 primary goals</h2>
      <p className="text-sm text-slate-500 mt-1">What matters most for the new website?</p>
      <div className="grid sm:grid-cols-2 gap-2 mt-6">
        {GOALS.map((g) => {
          const on = value.goals.includes(g);
          return (
            <button key={g} type="button" onClick={() => toggle(g)}
              data-testid={`goal-${g.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              className={`text-left px-4 py-3 rounded-md border transition ${on
                  ? "bg-sky-50 border-sky-500 text-sky-900 font-semibold"
                  : "bg-white border-slate-200 hover:border-slate-300 text-slate-700"}`}>
              {g}
              {on && <CheckCircle2 size={14} className="inline-block ml-1.5 text-sky-600" />}
            </button>
          );
        })}
      </div>
      <div className="flex justify-between mt-6">
        <Button variant="outline" onClick={onPrev} disabled={busy}><ChevronLeft size={16} className="mr-1" /> Back</Button>
        <Button onClick={onNext} disabled={busy || value.goals.length === 0} className="bg-slate-900 hover:bg-slate-800" data-testid="wizard-next-2">
          {busy ? <Loader2 className="mr-2 animate-spin" size={16} /> : null}
          Continue <ChevronRight size={16} className="ml-1" />
        </Button>
      </div>
    </div>
  );
}

function Step3({ vertical, value, onChange, onPrev, onNext, busy }) {
  const set = (k) => (e) => onChange({ ...value, [k]: e.target ? e.target.value : e });
  return (
    <div>
      <h2 className="text-2xl font-black text-slate-900">Website needs</h2>
      <p className="text-sm text-slate-500 mt-1">Skip anything you don't have yet. We'll figure it out with you.</p>
      <div className="grid gap-4 mt-6">
        <Field label="Primary services or products"><textarea rows={3} className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm" value={value.primary_services} onChange={set("primary_services")} placeholder="e.g. Drain cleaning, water heaters, emergency plumbing" data-testid="input-services" /></Field>
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Service locations"><Input value={value.service_locations} onChange={set("service_locations")} placeholder="Suffolk County, Long Island" /></Field>
          <Field label="Preferred colors / style"><Input value={value.preferred_colors} onChange={set("preferred_colors")} placeholder="navy blue, clean, modern" /></Field>
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <label className="flex items-center gap-2 text-sm text-slate-700">
            <Checkbox checked={value.has_logo} onCheckedChange={(v) => onChange({ ...value, has_logo: !!v })} /> I have a logo I can share
          </label>
          <label className="flex items-center gap-2 text-sm text-slate-700">
            <Checkbox checked={value.has_photos} onCheckedChange={(v) => onChange({ ...value, has_photos: !!v })} /> I have photos or video of the business
          </label>
        </div>
        <Field label="Example websites you like"><Input value={value.example_websites} onChange={set("example_websites")} placeholder="Paste URLs (comma-separated)" /></Field>
        <Field label="Desired launch date"><Input type="date" value={value.launch_date_target} onChange={set("launch_date_target")} /></Field>
      </div>
      <div className="flex justify-between mt-6">
        <Button variant="outline" onClick={onPrev} disabled={busy}><ChevronLeft size={16} className="mr-1" /> Back</Button>
        <Button onClick={onNext} disabled={busy} className="bg-slate-900 hover:bg-slate-800" data-testid="wizard-next-3">Continue <ChevronRight size={16} className="ml-1" /></Button>
      </div>
    </div>
  );
}

function Step4({ value, onChange, onPrev, onNext, busy }) {
  const canSubmit = value.consent_terms && value.consent_privacy && value.agrees_to_starter_scope;
  return (
    <div>
      <h2 className="text-2xl font-black text-slate-900">Readiness & consent</h2>
      <p className="text-sm text-slate-500 mt-1">Last step — this becomes your program agreement.</p>
      <div className="mt-6 grid gap-4">
        <Field label="Business stage">
          <select value={value.business_stage} onChange={(e) => onChange({ ...value, business_stage: e.target.value })}
            className="w-full h-10 border border-slate-300 rounded-md px-3 text-sm">
            <option value="operating">Currently operating</option>
            <option value="prelaunch">Prelaunch / opening soon</option>
            <option value="idea">Idea stage — not yet operating</option>
          </select>
        </Field>
        <label className="flex items-center gap-2 text-sm text-slate-700">
          <Checkbox checked={value.owns_domain === true} onCheckedChange={(v) => onChange({ ...value, owns_domain: !!v })} /> I already own the domain
        </label>
        <label className="flex items-center gap-2 text-sm text-slate-700">
          <Checkbox checked={value.needs_ecommerce} onCheckedChange={(v) => onChange({ ...value, needs_ecommerce: !!v, scope_type: v ? "advanced" : value.scope_type })} /> I need ecommerce / bookings / payments (advanced scope)
        </label>
        <label className="flex items-center gap-2 text-sm text-slate-700">
          <Checkbox checked={value.can_verify_facts} onCheckedChange={(v) => onChange({ ...value, can_verify_facts: !!v })} /> I will verify every business fact before publication
        </label>

        <div className="border-t border-slate-200 pt-4 space-y-3">
          <label className="flex items-start gap-2 text-sm text-slate-800">
            <Checkbox checked={value.agrees_to_starter_scope} onCheckedChange={(v) => onChange({ ...value, agrees_to_starter_scope: !!v })} data-testid="consent-scope" />
            <span>I agree to the <strong>starter scope</strong>: up to 5 pages, one design direction, one AI-assisted revision round.</span>
          </label>
          <label className="flex items-start gap-2 text-sm text-slate-800">
            <Checkbox checked={value.consent_terms} onCheckedChange={(v) => onChange({ ...value, consent_terms: !!v })} data-testid="consent-terms" />
            <span>I accept the WebsiteTech program terms and understand no charges apply to the free preview.</span>
          </label>
          <label className="flex items-start gap-2 text-sm text-slate-800">
            <Checkbox checked={value.consent_privacy} onCheckedChange={(v) => onChange({ ...value, consent_privacy: !!v })} data-testid="consent-privacy" />
            <span>I've read the privacy notice — I can request deletion of my application data at any time.</span>
          </label>
          <label className="flex items-start gap-2 text-sm text-slate-500">
            <Checkbox checked={value.consent_marketing} onCheckedChange={(v) => onChange({ ...value, consent_marketing: !!v })} data-testid="consent-marketing" />
            <span>(Optional) Send me useful WebsiteTech tips. Marketing consent is separate from my application.</span>
          </label>
        </div>
      </div>
      <div className="flex justify-between mt-6">
        <Button variant="outline" onClick={onPrev} disabled={busy}><ChevronLeft size={16} className="mr-1" /> Back</Button>
        <Button onClick={onNext} disabled={busy || !canSubmit} className="bg-slate-900 hover:bg-slate-800" data-testid="wizard-submit">
          {busy ? <Loader2 className="mr-2 animate-spin" size={16} /> : null}
          Submit & verify email <ArrowRight size={16} className="ml-1" />
        </Button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
function OtpStep({ appId, publicToken, onVerified }) {
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [sentOnce, setSentOnce] = useState(false);

  useEffect(() => {
    (async () => {
      if (!appId || sentOnce) return;
      try {
        await launchOtpSend(appId);
        toast.success("Verification code sent to your email.");
        setSentOnce(true);
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Could not send code.");
      }
    })();
  }, [appId, sentOnce]);

  const verify = async () => {
    setBusy(true);
    try {
      await launchOtpVerify(appId, code.trim());
      toast.success("Email verified — analysing your business now…");
      onVerified();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Wrong code — try again.");
    } finally { setBusy(false); }
  };

  const resend = async () => {
    try { await launchOtpSend(appId); toast.success("New code sent."); }
    catch (e) { toast.error(e?.response?.data?.detail || "Please wait a minute."); }
  };

  return (
    <div className="min-h-screen bg-slate-50 py-16 px-4" data-testid="launch-otp">
      <div className="max-w-md mx-auto">
        <Card className="p-8 bg-white text-center">
          <div className="mx-auto w-12 h-12 rounded-full bg-sky-100 flex items-center justify-center">
            <Mail size={22} className="text-sky-700" />
          </div>
          <h2 className="text-2xl font-black text-slate-900 mt-4">Check your inbox</h2>
          <p className="text-sm text-slate-500 mt-1">
            We just sent a 6-digit code. Enter it below to finish the application.
          </p>
          <Input value={code} onChange={(e) => setCode(e.target.value.replace(/[^0-9]/g, "").slice(0, 6))}
            placeholder="123456" data-testid="otp-input"
            className="mt-5 text-center text-2xl tracking-[.5em] font-mono h-14" />
          <Button onClick={verify} disabled={busy || code.length !== 6}
            className="mt-4 w-full bg-slate-900 hover:bg-slate-800" data-testid="otp-verify">
            {busy ? <Loader2 className="mr-2 animate-spin" size={16} /> : <Lock size={14} className="mr-2" />}
            Verify & analyse
          </Button>
          <button onClick={resend} className="mt-3 text-xs text-slate-500 hover:text-sky-700">
            Didn't get it? Resend code
          </button>
        </Card>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
function DoneCard({ publicToken, onView }) {
  return (
    <div className="min-h-screen bg-slate-50 py-16 px-4" data-testid="launch-done">
      <div className="max-w-lg mx-auto">
        <Card className="p-8 bg-white text-center">
          <div className="mx-auto w-14 h-14 rounded-full bg-emerald-100 flex items-center justify-center">
            <CheckCircle2 size={26} className="text-emerald-600" />
          </div>
          <h2 className="text-2xl font-black text-slate-900 mt-4">Your preview is being generated</h2>
          <p className="text-sm text-slate-500 mt-2">
            Our AI is analysing your business now. A homepage draft, page plan and readiness score will appear in seconds.
          </p>
          <Button onClick={onView} className="mt-6 bg-slate-900 hover:bg-slate-800" data-testid="launch-view-preview">
            View my preview <ArrowRight size={16} className="ml-1" />
          </Button>
          <div className="mt-4 text-[11px] text-slate-400 font-mono">Bookmark: /launch/preview/{publicToken}</div>
        </Card>
      </div>
    </div>
  );
}
